import { cn } from "@/lib/utils";
import { ANALYSES } from "./analyses";

const NUMBERED_TEMPLATE_CODES = new Set(ANALYSES.map((a) => a.templateCode));

/**
 * The in-house analyses whose tubes are tracked by number: the lab finds a
 * tube by the number on its cap, so the requester writes it there as soon as
 * the request exists.
 */
export function isNumberedAnalysis(templateCode: string | null | undefined) {
  return templateCode != null && NUMBERED_TEMPLATE_CODES.has(templateCode);
}

export const CAP_LABEL_HINT = "Write this number on the tube cap";

/**
 * A sample number, as it should be written on the cap. The "cap" prefix is the
 * instruction itself, so the tag reads the same wherever it shows up.
 */
export function SampleNumberTag({
  id,
  className,
}: {
  id: number;
  className?: string;
}) {
  return (
    <span
      title={`${CAP_LABEL_HINT}: ${id}`}
      className={cn(
        "inline-flex items-baseline gap-1 border border-primary/40 bg-farmless-green-1 px-1 text-olive-12",
        className,
      )}
    >
      <span className="text-xs text-olive-10">cap</span>
      <span className="font-mono font-semibold tabular-nums">{id}</span>
    </span>
  );
}

/** Open, still-requested work of a numbered analysis: its tube needs the cap. */
export function showsCapNumber(test: {
  test_template_code?: string | null;
  status: string;
  request_withdrawn_at?: Date | string | null;
}): boolean {
  return (
    isNumberedAnalysis(test.test_template_code) &&
    test.request_withdrawn_at == null &&
    test.status !== "CANCELLED"
  );
}

/**
 * Who reads the cap: the person the requests are assigned to when they all
 * share one, otherwise whoever runs the analysis. Requests made inline carry
 * no assignee, so they read the general form.
 */
export function capReader(assignees: (string | null | undefined)[]): string {
  const names = new Set(assignees.filter((name): name is string => !!name));
  if (names.size === 1 && assignees.every((name) => !!name)) {
    return [...names][0];
  }
  return names.size > 0
    ? "Whoever it is assigned to"
    : "Whoever runs the analysis";
}
